import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardCheck,
  Truck,
  Check,
  X as XIcon,
  AlertTriangle,
  ChevronLeft,
} from "lucide-react";

const TYPES = [
  { key: "pre_trip", label: "Pre-Trip", color: "bg-emerald-500" },
  { key: "post_trip", label: "Post-Trip", color: "bg-sky-500" },
  { key: "weekly", label: "Weekly", color: "bg-amber-500" },
  { key: "monthly", label: "Monthly", color: "bg-violet-500" },
];

const FUEL_OPTIONS = ["F", "3/4", "1/2", "1/4", "E"];

export default function DriverInspection() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1=type, 2=vehicle, 3=checklist, 4=submit
  const [vehicles, setVehicles] = useState([]);
  const [inspType, setInspType] = useState("pre_trip");
  const [vid, setVid] = useState("");
  const [vehicle, setVehicle] = useState(null);
  const [template, setTemplate] = useState([]);
  const [items, setItems] = useState([]);
  const [odo, setOdo] = useState("");
  const [fuel, setFuel] = useState("");
  const [defects, setDefects] = useState("");
  const [busy, setBusy] = useState(false);

  // Step 2: load my vehicles
  useEffect(() => {
    api
      .get("/vehicle-ops/driver/vehicles")
      .then(({ data }) => setVehicles(data || []))
      .catch(() => toast.error("Could not load vehicles"));
  }, []);

  // Step 3: load template when type chosen
  const goStep3 = async () => {
    if (!vid) {
      toast.error("Select your vehicle");
      return;
    }
    setVehicle(vehicles.find((v) => v.id === vid));
    try {
      const { data } = await api.get(
        `/vehicle-ops/inspections/template?insp_type=${inspType}`,
      );
      setTemplate(data.items);
      setItems(
        data.items.map((it) => ({
          key: it.key,
          label: it.label,
          status: "pass",
          notes: "",
        })),
      );
      setStep(3);
    } catch (e) {
      toast.error("Could not load checklist");
    }
  };

  const setStatus = (idx, st) => {
    setItems((arr) =>
      arr.map((it, i) => (i === idx ? { ...it, status: st } : it)),
    );
  };
  const setNotes = (idx, n) => {
    setItems((arr) =>
      arr.map((it, i) => (i === idx ? { ...it, notes: n } : it)),
    );
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/vehicle-ops/driver/vehicles/${vid}/inspections`, {
        insp_type: inspType,
        date: new Date().toISOString().slice(0, 10),
        odometer_km: odo ? parseFloat(odo) : null,
        fuel_level: fuel || null,
        items,
        defects_found: defects || null,
      });
      toast.success("Inspection submitted");
      nav("/driver");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  /* -------- Step 1: choose type -------- */
  if (step === 1) {
    return (
      <div className="space-y-5" data-testid="driver-inspection">
        <div className="flex items-center gap-2">
          <button
            onClick={() => nav("/driver")}
            className="p-2 -ml-2"
            data-testid="driver-insp-back"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Step 1 of 3
            </div>
            <h1
              className="text-xl font-bold text-slate-900"
              style={{ fontFamily: "Chivo" }}
            >
              Inspection Type
            </h1>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3">
          {TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setInspType(t.key);
                setStep(2);
              }}
              data-testid={`driver-insp-type-${t.key}`}
              className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 active:scale-[0.99] transition flex items-center gap-4"
            >
              <div
                className={`h-12 w-12 rounded-2xl ${t.color} flex items-center justify-center text-white`}
              >
                <ClipboardCheck className="h-6 w-6" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-base font-bold text-slate-900">
                  {t.label} Inspection
                </div>
                <div className="text-[11px] text-slate-500">
                  Standard safety checklist
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  /* -------- Step 2: choose vehicle -------- */
  if (step === 2) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <button onClick={() => setStep(1)} className="p-2 -ml-2">
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Step 2 of 3 · {TYPES.find((t) => t.key === inspType)?.label}
            </div>
            <h1
              className="text-xl font-bold text-slate-900"
              style={{ fontFamily: "Chivo" }}
            >
              Select Vehicle
            </h1>
          </div>
        </div>
        <div className="space-y-2">
          {vehicles.map((v) => (
            <button
              key={v.id}
              onClick={() => setVid(v.id)}
              data-testid={`driver-insp-vehicle-${v.id}`}
              className={`w-full rounded-xl ring-1 p-3 text-left transition flex items-center gap-3 ${
                vid === v.id
                  ? "ring-bravura-500 bg-bravura-50"
                  : "ring-slate-200 bg-white"
              }`}
            >
              <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Truck className="h-5 w-5 text-slate-700" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-900">
                  {v.truck_no}
                </div>
                {(v.make || v.model) && (
                  <div className="text-[11px] text-slate-500">
                    {v.make} {v.model}
                  </div>
                )}
              </div>
              {vid === v.id && (
                <Check className="h-4 w-4 text-bravura-700" />
              )}
            </button>
          ))}
          {vehicles.length === 0 && (
            <div className="rounded-xl bg-white ring-1 ring-slate-200 p-5 text-center text-sm text-slate-500">
              No vehicles available.
            </div>
          )}
        </div>
        <button
          disabled={!vid}
          onClick={goStep3}
          data-testid="driver-insp-next"
          className="w-full rounded-xl bg-slate-900 text-white py-3 font-semibold disabled:bg-slate-300"
        >
          Continue →
        </button>
      </div>
    );
  }

  /* -------- Step 3: checklist + submit -------- */
  return (
    <div className="space-y-4 pb-32">
      <div className="flex items-center gap-2">
        <button onClick={() => setStep(2)} className="p-2 -ml-2">
          <ChevronLeft className="h-5 w-5 text-slate-600" />
        </button>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            Step 3 of 3 · {vehicle?.truck_no}
          </div>
          <h1
            className="text-xl font-bold text-slate-900"
            style={{ fontFamily: "Chivo" }}
          >
            {TYPES.find((t) => t.key === inspType)?.label} Checklist
          </h1>
        </div>
      </div>

      {/* Top bits */}
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Odometer (km)
          </span>
          <input
            type="number"
            value={odo}
            onChange={(e) => setOdo(e.target.value)}
            className="mt-1 w-full rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm"
            data-testid="driver-insp-odo"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Fuel level
          </span>
          <div className="grid grid-cols-5 gap-1 mt-1">
            {FUEL_OPTIONS.map((f) => (
              <button
                key={f}
                onClick={() => setFuel(f)}
                data-testid={`driver-insp-fuel-${f}`}
                className={`rounded-lg py-2 text-xs font-bold ring-1 ${
                  fuel === f
                    ? "bg-bravura text-white ring-bravura"
                    : "bg-white text-slate-700 ring-slate-200"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </label>
      </div>

      {/* Checklist */}
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div
            key={it.key}
            className="rounded-xl bg-white ring-1 ring-slate-200 p-3"
            data-testid={`driver-insp-item-${it.key}`}
          >
            <div className="text-sm font-semibold text-slate-900 mb-2">
              {it.label}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { v: "pass", label: "Pass", icon: Check, on: "bg-emerald-500 text-white ring-emerald-500" },
                { v: "conditional", label: "Caution", icon: AlertTriangle, on: "bg-amber-500 text-white ring-amber-500" },
                { v: "fail", label: "Fail", icon: XIcon, on: "bg-red-500 text-white ring-red-500" },
              ].map((b) => {
                const Ic = b.icon;
                const active = it.status === b.v;
                return (
                  <button
                    key={b.v}
                    onClick={() => setStatus(idx, b.v)}
                    data-testid={`driver-insp-${it.key}-${b.v}`}
                    className={`flex items-center justify-center gap-1 py-2 rounded-lg ring-1 text-xs font-semibold ${
                      active ? b.on : "bg-white text-slate-600 ring-slate-200"
                    }`}
                  >
                    <Ic className="h-3.5 w-3.5" /> {b.label}
                  </button>
                );
              })}
            </div>
            {it.status !== "pass" && (
              <input
                value={it.notes}
                onChange={(e) => setNotes(idx, e.target.value)}
                placeholder="Add a note…"
                className="mt-2 w-full rounded-lg ring-1 ring-slate-200 px-3 py-2 text-xs"
              />
            )}
          </div>
        ))}
      </div>

      {/* Overall comment */}
      <label className="block">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          Major defects / accidents (optional)
        </span>
        <textarea
          value={defects}
          onChange={(e) => setDefects(e.target.value)}
          rows={2}
          className="mt-1 w-full rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm"
          data-testid="driver-insp-defects"
        />
      </label>

      {/* Bottom sticky CTA */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3 z-40">
        <button
          disabled={busy}
          onClick={submit}
          data-testid="driver-insp-submit"
          className="w-full rounded-xl bg-slate-900 text-white py-3 font-bold disabled:bg-slate-400"
        >
          {busy ? "Submitting…" : "Submit Inspection"}
        </button>
      </div>
    </div>
  );
}
